import { watch } from 'vue'

import LEGAL_CONSENTS from '@/queries/LegalConsents.gql'

import { getInstance } from '../auth/index'

const authGuard = (fn) => {
  const authService = getInstance()

  if (!authService.loading) {
    return fn()
  }

  const stopWatch = watch(
    () => authService.loading,
    (loading) => {
      if (loading === false) {
        fn()
        stopWatch()
      }
    },
    { immediate: true }
  )
}

const authenticated = (to, from, next) => {
  const authService = getInstance()

  authGuard(() => {
    if (authService.isAuthenticated) {
      return next()
    }

    authService.rememberLocationBeforeLogin()
    return next({ name: 'login' })
  })
}

const publicOnly = (to, from, next) => {
  const authService = getInstance()

  authGuard(() => {
    if (!authService.isAuthenticated) {
      return next()
    }

    return next({ name: 'dashboard', replace: true })
  })
}

async function hasAcceptedLegalConsents(apollo, companyId) {
  const { data } = await apollo.defaultClient.query({
    query: LEGAL_CONSENTS,
    variables: { id: companyId },
    fetchPolicy: 'network-only', // always the current state, never a stale cache
  })
  return (data?.company?.legalConsents?.length ?? 0) > 0
}

/**
 * accepted: true  → page needs accepted consents (welcome), otherwise → consents page
 * accepted: false → page is for not-yet-accepted consents (consents page), otherwise → onboarding
 */
const legalConsents =
  (apollo, { accepted }) =>
  (to, from, next) => {
    const authService = getInstance()

    authGuard(async () => {
      const companyId = authService.user?.companyId
      if (!companyId) {
        return next({ name: 'login' })
      }

      try {
        const isAccepted = await hasAcceptedLegalConsents(apollo, companyId)
        if (isAccepted === accepted) {
          return next()
        }

        return next(
          accepted
            ? { name: 'beta-legal-consents', replace: true }
            : { name: 'onboarding', replace: true }
        )
      } catch (error) {
        console.error('[legalConsents guard]', error)
        return next({ name: 'onboarding', replace: true })
      }
    })
  }

export { authenticated, authGuard, legalConsents as legalConsentsGuard, publicOnly }
